import { useState, useEffect } from "react";
import axios from "axios";

interface DeepSearchParams {
  words: string;
  beginAt?: string;
  endAt?: string;
  group?: string[];
  chamber?: string[];
}

interface DeepSearchResult {
  id: string;
  text: string;
  date: string;
  deputy: string;
  description: string;
  group: string;
  chamber: string;
  type: string;
  // Note: chamber appears to be "AN" for Assemblée Nationale
  // group appears to be abbreviated codes like "EPR", "RE", "LFI-NFP", etc.
}

interface UseDeepSearchReturn {
  data: DeepSearchResult[] | null;
  loading: boolean;
  error: string | null;
  search: (params: DeepSearchParams) => Promise<void>;
  reset: () => void;
}

const useDeepSearch = (): UseDeepSearchReturn => {
  const [data, setData] = useState<DeepSearchResult[] | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const search = async (params: DeepSearchParams) => {
    // Don't search if words is empty
    if (!params.words.trim()) {
      setError("Le champ de recherche est obligatoire");
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Prepare the request body
      const requestBody: any = {
        words: params.words.trim(),
      };

      // Add optional parameters only if they have values
      if (params.beginAt) {
        requestBody.beginAt = params.beginAt;
      }

      if (params.endAt) {
        requestBody.endAt = params.endAt;
      }

      if (params.group && params.group.length > 0) {
        requestBody.group = params.group;
      }

      if (params.chamber && params.chamber.length > 0) {
        requestBody.chamber = params.chamber;
      }

      console.log("Deep search request:", requestBody);

      const response = await axios.post(
        "http://localhost:2000/deepsearch",
        requestBody,
        {
          headers: {
            "Content-Type": "application/json",
          },
          timeout: 30000, // 30 second timeout
        }
      );

      setData(response.data);
    } catch (err: any) {
      console.error("Deep search error:", err);

      if (err.code === "ECONNABORTED") {
        setError("La recherche a pris trop de temps. Veuillez réessayer.");
      } else if (err.response?.status === 400) {
        setError("Paramètres de recherche invalides.");
      } else if (err.response?.status === 500) {
        setError("Erreur serveur. Veuillez réessayer plus tard.");
      } else if (err.message === "Network Error") {
        setError(
          "Impossible de se connecter au serveur. Vérifiez votre connexion."
        );
      } else {
        setError(
          err.response?.data?.message ||
            "Une erreur est survenue lors de la recherche."
        );
      }
    } finally {
      setLoading(false);
    }
  };

  const reset = () => {
    setData(null);
    setError(null);
    setLoading(false);
  };

  return {
    data,
    loading,
    error,
    search,
    reset,
  };
};

export default useDeepSearch;
